/**
 * usePhotoCapture - Handles photo capture for checkpoint conversations
 *
 * Manages the flow of:
 * 1. Detecting when a new photo is taken
 * 2. Preventing duplicate processing
 * 3. Calling the appropriate callback with photo data
 */
import { useEffect, useRef, useCallback } from "react"
import { useSelector, useDispatch } from "react-redux"
import { clearLastPhoto, clearPhotos } from "@data/redux/actions/camera"
import { formIds } from "@data/constants/formConstants"

/**
 * Whether the photo in the store is the conversation's to take — null when it is not.
 * A photo taken from the chat is the student's message to the tutor: the conversation
 * takes it only when it opened the chat camera itself to ask for it. Otherwise a quiz
 * or a raised hand on screen swallowed it and the tutor never got it.
 */
export const claimedPhoto = ({ formId, isStartingBlock, expectingChatPhoto }) => {
    if (isStartingBlock && formId === formIds.CHECKPOINT) return { isCheckpointPhoto: true }
    if (formId === formIds.NEW_MATERIAL) return { isCheckpointPhoto: false }
    if (formId === "chat" && expectingChatPhoto) return { isCheckpointPhoto: false }
    return null
}

/**
 * @param {Object} options
 * @param {Function} options.onPhotoTaken - Callback when photo is captured (photo, isCheckpointPhoto)
 * @param {boolean} options.isStartingBlock - Whether this is a starting block context
 * @param {boolean} options.enabled - Whether to listen for photos
 * @returns {Object} { clearProcessed, clearPhoto, expectChatPhoto, dropChatPhotoRequest, hasPhoto }
 */
export const usePhotoCapture = ({
    onPhotoTaken,
    isStartingBlock = false,
    enabled = true
}) => {
    const dispatch = useDispatch()
    const processedPhotosRef = useRef(new Set())
    const expectingChatPhotoRef = useRef(false)

    // Redux selectors
    const lastPhoto = useSelector(state => state.camera?.lastPhoto)
    const lastPhotos = useSelector(state => state.camera?.lastPhotos)
    const lastPhotoFormId = useSelector(state => state.camera?.formId)

    // Get the actual photo (could be in lastPhoto or lastPhotos[0])
    const photo = lastPhoto || lastPhotos?.[0]

    useEffect(() => {
        if (!enabled || !photo) return

        // Generate a unique ID for this photo
        const photoId = typeof photo === "string" ? photo : photo?.uri
        if (!photoId) return

        // Prevent duplicate processing
        if (processedPhotosRef.current.has(photoId)) return

        const claim = claimedPhoto({
            formId: lastPhotoFormId,
            isStartingBlock,
            expectingChatPhoto: expectingChatPhotoRef.current
        })
        // Left in the store for its owner (the chat picks it up when it remounts)
        if (!claim) return

        // Mark as processed
        processedPhotosRef.current.add(photoId)
        if (lastPhotoFormId === "chat") expectingChatPhotoRef.current = false

        // Clear from Redux (both lastPhoto and lastPhotos)
        dispatch(clearLastPhoto())
        dispatch(clearPhotos())

        // Notify parent
        onPhotoTaken?.(photo, claim.isCheckpointPhoto)

    }, [photo, lastPhotoFormId, enabled, isStartingBlock, onPhotoTaken, dispatch])

    // Call right before opening the chat camera to get the student's answer
    const expectChatPhoto = useCallback(() => {
        expectingChatPhotoRef.current = true
    }, [])

    // Call when the student leaves that chat without answering (camera cancelled)
    const dropChatPhotoRequest = useCallback(() => {
        expectingChatPhotoRef.current = false
    }, [])

    // Method to clear processed photos (useful for retries)
    const clearProcessed = useCallback(() => {
        processedPhotosRef.current.clear()
    }, [])

    // Method to manually clear the last photo from Redux (both lastPhoto and lastPhotos)
    const clearPhoto = useCallback(() => {
        dispatch(clearLastPhoto())
        dispatch(clearPhotos())
    }, [dispatch])

    return {
        clearProcessed,
        clearPhoto,
        expectChatPhoto,
        dropChatPhotoRequest,
        hasPhoto: !!photo
    }
}

export default usePhotoCapture
